/**
 * HeaderNotesBadge.js
 *
 * A small 3D-style note icon that lives in the top navigation, right next to
 * the account plan badge. It only appears when the active account has at least
 * one "Header notes" entry (see CockpitDataVault → Notes & Files → Header notes).
 *
 * Clicking it opens a compact popover showing the account's most important
 * topics: a short intro text followed by bullet points. Content is authored in
 * the cockpit's Notes & Files vault, using the same lightweight markup.
 *
 * Data source (Realtime DB):
 *   World/${worldId}/dataVault/entries/*  where category === 'header-notes'
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ref as dbRef, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { HEADER_NOTES_CATEGORY_ID } from './CockpitDataVault';
import './HeaderNotesBadge.css';

// ── Inline markup: **bold**, _italic_ ──────────────────────────────────────
function renderInline(text, keyPrefix) {
  if (!text) return [text];
  const parts = text.split(/(\*\*[^*]+\*\*|_[^_]+_)/g);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (/^_[^_]+_$/.test(part)) return <em key={key}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

// ── Block pass: intro paragraphs + bullet lists (matches the vault subset) ──
function renderNoteBody(content, keyPrefix) {
  if (!content || !content.trim()) return null;
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null;
  let para = null;

  const flushList = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  const flushPara = () => { if (para) { blocks.push({ type: 'p', lines: para }); para = null; } };

  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) { flushList(); flushPara(); return; }
    // Treat markdown headings as plain emphasis lines so header notes stay simple.
    const clean = line.replace(/^#{1,3}\s+/, '');
    if (/^[-*]\s+/.test(clean)) { flushPara(); (list = list || []).push(clean.replace(/^[-*]\s+/, '')); return; }
    flushList(); (para = para || []).push(clean);
  });
  flushList();
  flushPara();

  return blocks.map((b, i) => {
    const k = `${keyPrefix}-b${i}`;
    if (b.type === 'ul') {
      return (
        <ul key={k} className="hnBadge__bullets">
          {b.items.map((it, j) => <li key={`${k}-${j}`}>{renderInline(it, `${k}-${j}`)}</li>)}
        </ul>
      );
    }
    return (
      <p key={k} className="hnBadge__intro">
        {b.lines.map((ln, j) => (
          <React.Fragment key={`${k}-${j}`}>
            {renderInline(ln, `${k}-${j}`)}
            {j < b.lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

// ── Live subscription to the account's header notes ─────────────────────────
function useHeaderNotes(worldId) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!worldId) { setNotes([]); return undefined; }
    const entriesRef = dbRef(database, `World/${worldId}/dataVault/entries`);
    const unsub = onValue(entriesRef, (snap) => {
      const val = snap.exists() ? snap.val() : {};
      const list = Object.entries(val)
        .map(([id, e]) => ({ id, ...e }))
        .filter((e) => e.category === HEADER_NOTES_CATEGORY_ID && (e.content?.trim() || e.title?.trim()))
        .sort((a, b) => {
          if (!!b.pinned - !!a.pinned !== 0) return !!b.pinned - !!a.pinned;
          return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
        });
      setNotes(list);
    }, (err) => {
      console.warn('[HeaderNotes] subscribe error', err);
      setNotes([]);
    });
    return () => unsub();
  }, [worldId]);

  return notes;
}

export default function HeaderNotesBadge({ worldId }) {
  const notes = useHeaderNotes(worldId);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasNotes = notes.length > 0;
  const totalPoints = useMemo(
    () => notes.reduce((sum, n) => {
      const bullets = (n.content || '').split('\n').filter((l) => /^\s*[-*]\s+/.test(l)).length;
      return sum + bullets;
    }, 0),
    [notes]
  );

  if (!hasNotes) return null;

  return (
    <div className="hnBadge" ref={wrapRef}>
      <button
        type="button"
        className={`hnBadge__btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Account notes"
      >
        <span className="hnBadge__note" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient id="hnPaperGrad" x1="4" y1="3" x2="18" y2="20" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#8fb8d6" />
                <stop offset="0.55" stopColor="#6897B7" />
                <stop offset="1" stopColor="#4d7c9e" />
              </linearGradient>
            </defs>
            <path
              className="hnBadge__notePaper"
              d="M4.5 4.5a1.5 1.5 0 0 1 1.5-1.5h12a1.5 1.5 0 0 1 1.5 1.5v9.2L14.2 20H6a1.5 1.5 0 0 1-1.5-1.5V4.5Z"
              fill="url(#hnPaperGrad)"
            />
            <path className="hnBadge__noteFold" d="M19.5 13.7H15.7a1.5 1.5 0 0 0-1.5 1.5V20Z" fill="#3f6a89" />
            <path className="hnBadge__noteLines" d="M8 8h8M8 11.5h6" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="hnBadge__pop" role="dialog" aria-label="Account notes">
          <div className="hnBadge__popArrow" aria-hidden />
          <div className="hnBadge__popHead">
            <span className="hnBadge__popHeadIcon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v10l-6 6H5a1 1 0 0 1-1-1V4Z" />
                <path d="M20 14h-5a1 1 0 0 0-1 1v5" />
              </svg>
            </span>
            <span className="hnBadge__popTitle">Account notes</span>
            {totalPoints > 0 && (
              <span className="hnBadge__popCount">{totalPoints} {totalPoints === 1 ? 'point' : 'points'}</span>
            )}
          </div>

          <div className="hnBadge__popBody">
            {notes.map((n, idx) => (
              <div key={n.id} className={`hnBadge__note-entry ${idx > 0 ? 'has-divider' : ''}`}>
                {n.title && n.title.trim() && n.title.trim().toLowerCase() !== 'untitled' && (
                  <div className="hnBadge__noteTitle">{n.title}</div>
                )}
                {renderNoteBody(n.content, n.id)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
